import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import API from '../lib/api';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    // Guard against React 18 StrictMode double-invoke — the verify endpoint
    // is single-use, so re-firing consumes the token and flips the UI to error.
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus('error');
      setMessage('Verification token is missing.');
      return;
    }
    (async () => {
      try {
        const { data } = await API.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setEmail(data.email || '');
        setStatus('success');
      } catch (err) {
        const detail = err.response?.data?.detail || 'Verification failed.';
        // If the visitor is already logged in AND their account is verified,
        // treat this as success — the link was almost certainly consumed by
        // an earlier click (or replaced by a resend). No point scaring them
        // with a red "failed" screen when the underlying goal is done.
        const localToken = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
        if (localToken) {
          try {
            const { data: me } = await API.get('/auth/me');
            if (me?.user?.email_verified) {
              setEmail(me.user.email || '');
              setStatus('success');
              return;
            }
          } catch (_) { /* fall through to error */ }
        }
        setStatus('error');
        setMessage(detail);
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#04160d] flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-3xl bg-[#0a2416] border border-emerald-500/25 shadow-2xl p-8 text-center" data-testid="verify-email-page">
        <div className="text-emerald-300 font-serif text-2xl mb-1">SkyWay <span className="text-emerald-500">Green</span></div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-500/80 mb-8">Ecosystem Infrastructure</div>

        {status === 'loading' && (
          <div className="py-10">
            <Loader2 size={40} className="mx-auto animate-spin text-emerald-400" />
            <div className="mt-4 text-emerald-200/80 text-sm">Verifying your email…</div>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="verify-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-4">
              <CheckCircle2 size={36} className="text-emerald-400" />
            </div>
            <h1 className="font-serif text-2xl text-emerald-50 mb-2">Email Verified</h1>
            <p className="text-sm text-emerald-200/70 mb-6">
              {email ? <>Your email <b className="text-emerald-100">{email}</b> is now verified.</> : 'Your email is now verified.'} You can access all portal features.
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-lime-400 to-emerald-400 hover:brightness-110 text-emerald-950 font-semibold px-6 py-3 text-sm transition"
            >
              Go to Portal
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="verify-error">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center mb-4">
              <XCircle size={36} className="text-red-400" />
            </div>
            <h1 className="font-serif text-2xl text-emerald-50 mb-2">Link No Longer Valid</h1>
            <p className="text-sm text-red-200/80 mb-3">{message}</p>
            <p className="text-xs text-emerald-200/70 mb-2">
              This can happen if:
            </p>
            <ul className="text-xs text-emerald-200/70 text-left list-disc pl-6 space-y-1 mb-4">
              <li>You already clicked this link once — your email is likely already verified. Just <b>sign in</b>.</li>
              <li>A newer verification email was sent — only the latest link works.</li>
              <li>The link expired (48 hours).</li>
            </ul>
            <p className="text-xs text-emerald-200/60 mb-4">
              Still stuck? Sign in and use the "Resend Verification" button in the banner to get a fresh link.
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-full bg-emerald-900 hover:bg-emerald-800 text-emerald-50 font-medium px-6 py-3 text-sm transition"
            >
              Back to Portal
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
